/**
 * Bundle 80.2 Task 2 - Public testimonials showcase page.
 * URL: /testimonials
 *
 * Grid of quote cards, 12 per page, "Load more" pagination via cursor.
 * Filter chip strip at top for event formats (derived server-side).
 * Warm empty state.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import TestimonialCard from '@/components/TestimonialCard';

const BASE = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${BASE}/api` });

const TestimonialsPage = () => {
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [format, setFormat] = useState('all');
  const [error, setError] = useState('');

  const load = useCallback(async (reset = true) => {
    setLoading(true);
    setError('');
    try {
      const params = { limit: 12 };
      if (!reset && cursor) params.cursor = cursor;
      if (format !== 'all') params.format = format;
      const { data } = await api.get('/testimonials', { params });
      const fetched = data?.testimonials || [];
      setItems((prev) => (reset ? fetched : [...prev, ...fetched]));
      setCursor(data?.next_cursor || null);
      setHasMore(!!data?.has_more);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load testimonials');
    } finally {
      setLoading(false);
    }
  }, [cursor, format]);

  useEffect(() => {
    setItems([]);
    setCursor(null);
    setHasMore(false);
    // Reset + reload when format changes. `load` intentionally omitted
    // from deps because it closes over cursor which mutates within the
    // load itself; wrapping into useCallback with cursor dep would
    // trigger the effect on every fetch.
    (async () => {
      setLoading(true);
      setError('');
      try {
        const params = { limit: 12 };
        if (format !== 'all') params.format = format;
        const { data } = await api.get('/testimonials', { params });
        setItems(data?.testimonials || []);
        setCursor(data?.next_cursor || null);
        setHasMore(!!data?.has_more);
      } catch (e) {
        setError(e?.response?.data?.detail || 'Could not load testimonials');
      } finally {
        setLoading(false);
      }
    })();
  }, [format]);

  const formats = useMemo(() => {
    const set = new Set();
    items.forEach((i) => { if (i.event_format) set.add(i.event_format); });
    return Array.from(set).sort();
  }, [items]);

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="testimonials-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14">
        <div className="max-w-2xl">
          <div className="text-[11px] uppercase tracking-widest text-secondary-800">Real gatherings, real people</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-primary-900 mt-2">
            Guests on their Dostination nights.
          </h1>
          <p className="text-neutral-700 mt-4 leading-relaxed">
            Every quote below was submitted through our post-event survey and
            approved by the guest themselves. No photos, only their words.
          </p>
        </div>

        {formats.length > 1 ? (
          <div className="mt-8 flex flex-wrap gap-2" data-testid="testimonials-format-filter">
            <button
              type="button"
              onClick={() => setFormat('all')}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                format === 'all'
                  ? 'bg-primary-900 text-white border-primary-900'
                  : 'bg-white text-primary-800 border-neutral-200'
              }`}
              data-testid="format-chip-all"
            >
              All formats
            </button>
            {formats.map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setFormat(f)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors capitalize ${
                  format === f
                    ? 'bg-primary-900 text-white border-primary-900'
                    : 'bg-white text-primary-800 border-neutral-200'
                }`}
                data-testid={`format-chip-${f.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
              >
                {f}
              </button>
            ))}
          </div>
        ) : null}

        <div className="mt-10">
          {error ? <div className="text-rose-700 text-sm" data-testid="testimonials-error">{error}</div> : null}
          {items.length === 0 && !loading ? (
            <div className="text-center py-20 border border-dashed border-neutral-200 rounded-lg" data-testid="testimonials-empty">
              <div className="text-primary-900 font-display text-xl">Real stories from real Dostination guests coming soon.</div>
              <div className="text-neutral-600 text-sm mt-2">Come back after our next gathering.</div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="testimonials-grid">
              {items.map((t) => <TestimonialCard key={t.id} t={t} />)}
            </div>
          )}
        </div>

        {hasMore ? (
          <div className="flex justify-center mt-10">
            <Button
              variant="outline"
              onClick={() => load(false)}
              disabled={loading}
              data-testid="testimonials-load-more-btn"
            >
              {loading ? 'Loading...' : 'Load more'}
            </Button>
          </div>
        ) : null}

        <div className="mt-16 pt-6 border-t border-neutral-200">
          <div className="text-xs text-neutral-500">
            Testimonials are shared only with the guest&#39;s explicit consent and
            attribution choice. Guests can revoke at any time and the quote is
            removed within minutes.
            <Badge variant="outline" className="ml-2">Bundle 80.2</Badge>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestimonialsPage;
